<script setup lang="ts">
import { ELoginSuccessSource, EVisitorGamePlayBtnChannels } from '@/types/components'
import SVG_BANNER_RIGHT_TRIANGLE from '/assets/svg/banner-right-triangle.svg'

const props = withDefaults(defineProps<{
  isLoading: boolean
  progress: number | null
}>(), {
  isLoading: false,
  progress: 0,
})

const hookOutside = defineEmits(['tapPlay'])

let markLoginSuccessAutoPlay = false
const isAdsBlock = ref(false)
const { t } = useI18n()
const { isLogged, loginSource } = useUserInfo()
const playBtnGroupElement = ref<HTMLElement>()
const { setIsShowPersonalPopup, isShowPersonalPopup } = usePersonalPopup()

const loadingText = computed(() => `${t('common.loading')} ${((props.progress || 0) * 100).toFixed(0)}%`)

function toggleElementHoverStatus() {
  if (!isLogged.value)
    playBtnGroupElement.value?.classList.add('visitor-status')
  else
    playBtnGroupElement.value?.classList.remove('visitor-status')
}

function choosePlayType(type: EVisitorGamePlayBtnChannels) {
  if (type === EVisitorGamePlayBtnChannels.TRIAL) {
    // want to play trial
    hookOutside('tapPlay')
  }
  else {
    // want to login play
    if (isLogged.value) {
      hookOutside('tapPlay')
      return
    }
    markLoginSuccessAutoPlay = true
    loginSource.value = ELoginSuccessSource.GAME_DETAIL_VISITOR_PLAY_BTN
    setIsShowPersonalPopup(true)
  }
  window?.dataLayer?.push({ event: 'visitor-play-choose', type })
}

watch(() => isShowPersonalPopup.value, () => {
  if (!isShowPersonalPopup.value) {
    markLoginSuccessAutoPlay = false
    loginSource.value = ELoginSuccessSource.UNKNOWN
  }
})

watch(() => isLogged.value, () => {
  toggleElementHoverStatus()
  markLoginSuccessAutoPlay && hookOutside('tapPlay')
}, { immediate: true })

watchEffect(() => {
  if (playBtnGroupElement.value)
    toggleElementHoverStatus()
})

watch(() => props.isLoading, () => {
  if (!props.isLoading) {
    nextTick(() => {
      toggleElementHoverStatus()
    })
  }
})

onMounted(() => {
  isAdsBlock.value = !window.EASYFUN_ADS_CAN_RUN && process.env.NODE_ENV !== 'development'
})
</script>

<template>
  <div
    ref="playBtnGroupElement"
    class="mt-6 h-11 w-57.5 flex cursor-pointer select-none of-hidden rd-2 bg-green-5 transition-width-150 md:h-12"
    :class="{ 'w-180px!': isLoading, 'bg-gray-5!': isAdsBlock }"
  >
    <template v-if="!isLoading">
      <button
        class="play-on-browser min-w-57.5 w-fit cursor-pointer bg-transparent text-3.5 font-600 lh-3.5 transition-500"
        @click="isLogged ? $emit('tapPlay') : () => { }"
      >
        <SVG_BANNER_RIGHT_TRIANGLE v-if="!isLoading && !isAdsBlock" class="mie-2 size-4 v-end" />
        <span v-if="!isAdsBlock" class="text-3.5 c-#fff c-gray-1 font-600 lh-3.5">{{ t(`gameDetail.defaultStatusPlayBtn`) || 'Play on browser' }}</span>
        <span v-else class="text-3.5 c-#fff c-op-90 font-600 lh-3.5 underline">{{ $t('ads.blockTips') }}</span>
      </button>
      <div class="choose-btn-group min-w-57.5 w-fit flex of-hidden rd-2 transition-300 transition-ease-out">
        <button
          class="trial-btn w-40% flex flex-center flex-col bg-gray-5 px-1 transition-width-300"
          @click="choosePlayType(EVisitorGamePlayBtnChannels.TRIAL)"
        >
          <span class="text-4 c-#fff c-op-90 lh-1em font-[Bebas_Neue]">{{ t('common.play') || 'Play' }}</span>
          <small class="mt-0.5 text-3.5 c-#fff c-op-90 lh-1em">{{ t('gameDetail.visitorPlayBtn', { min: 30 }) }}</small>
        </button>

        <button
          class="login-play-btn flex flex-center flex-1 flex-col bg-green-5 px-1 transition-width-300"
          @click="choosePlayType(EVisitorGamePlayBtnChannels.LOGIN)"
        >
          <span class="text-4 c-gray-1 lh-1em font-[Bebas_Neue]">{{ t('common.login') || 'Login' }}</span>
          <small class="mt-0.5 text-3.5 c-gray-1 lh-1em">{{ t('gameDetail.userPlayBtn', { hour: 6 })
            || `6 hrs free/day` }}</small>
        </button>
      </div>
    </template>
    <div v-else class="w-full flex flex-center">
      <img class="mie-1 size-6" src="~/assets/images/apng/black-face-loading.png" alt="">
      <span class="min-w-20 text-3.5 c-gray-1 font-600">{{ loadingText }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@keyframes clear-bg {
  99% {
    background-color: rgba(204, 255, 0, 1);
  }

  100% {
    background-color: transparent;
  }
}

.visitor-status {
  &:hover {
    animation: clear-bg 1ms linear 300ms forwards;

    .play-on-browser,
    .choose-btn-group {
      transform: translateX(-100%);
    }
  }

  .trial-btn {
    &:hover {
      width: 46%;
    }
  }
}

:dir(rtl) {
  .visitor-status {
    &:hover {
      animation: clear-bg 1ms linear 300ms forwards;

      .play-on-browser,
      .choose-btn-group {
        transform: translateX(100%);
      }
    }
  }
}
</style>
